import React, { useCallback, useState } from "react";
import moment from "moment";
import {
  TableRow,
  TableCell,
  Table,
  TableContainer,
  TableBody,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Grid,
  Tooltip,
  IconButton,
} from "@material-ui/core";
import { EnhancedTableHead } from "./index";
import CustomChip from "../../../customChip/customChip";
import excelIcon from "../../../../assets/images/icons/excel.svg";
import axios from "../../../../axios";

const invoiceSummaryHeadCells = [
  { id: "i", numeric: false, disablePadding: false, label: "Sl.No" },
  {
    id: "company",
    numeric: false,
    disablePadding: false,
    label: "Company Name",
  },
  {
    id: "booking_ref",
    numeric: false,
    disablePadding: false,
    label: "Booking Ref",
  },
  {
    id: "customer",
    numeric: false,
    disablePadding: false,
    label: "Invoice No / Date",
  },
  {
    id: "subTotal",
    numeric: false,
    disablePadding: false,
    label: "Net Invoice",
  },
  {
    id: "invTotal",
    numeric: false,
    disablePadding: false,
    label: "Gross Total",
  },
  { id: "vat", numeric: false, disablePadding: false, label: "VAT" },
  { id: "hvatp", numeric: false, disablePadding: false, label: "HVAT 12%" },
  {
    id: "outstanding",
    numeric: false,
    disablePadding: false,
    label: "Outstanding",
  },
  {
    id: "paymentRef",
    numeric: false,
    disablePadding: false,
    label: "Payment Ref",
  },
  {
    id: "paymentDate",
    numeric: false,
    disablePadding: false,
    label: "Payment Date",
  },
  {
    id: "paidAmount",
    numeric: false,
    disablePadding: false,
    label: "Paid Amount",
  },
  { id: "paidAmount", numeric: false, disablePadding: false, label: "Notes" },
];

const styles = {
  fontSize: "13px",
  padding: "6px",
};

const CardItem = ({
  title,
  active,
  classes,
  data,
  isLoading,
  matches,
  hasData,
  values,
}) => {
  const [selected, setSelected] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("company");
  const [downloading, setDownloading] = useState(false);

  const handleRequestSort = useCallback(
    (event, property) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [setOrder, setOrderBy, orderBy],
  );

  const handleSelectAllClick = useCallback(
    (event) => {
      if (event.target.checked) {
        const newSelecteds = data.map((n) => n.name);
        setSelected(newSelecteds);
        return;
      }
      setSelected([]);
    },
    [setSelected],
  );

  const downloadHandler = async () => {
    try {
      setDownloading(true);
      let query = "";

      if (values.companyId) {
        query += `companyId=${values.companyId}`;
      }

      if (values.businessType) {
        query += `${query ? "&" : ""}businessType=${values.businessType}`;
      }

      if (values.fromDate) {
        query += `${query ? "&" : ""}fromDate=${values.fromDate}`;
      }

      if (values.toDate) {
        query += `${query ? "&" : ""}toDate=${values.toDate}`;
      }

      const response = await axios.get(
        `/api/v1/payments/invpayments/download?${query}`,
        {
          responseType: "blob", // 🔥 IMPORTANT
        },
      );

      // ✅ Create file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute(
        "download",
        `InvoicePaymentReport_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );

      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Card className={active}>
        <CardContent style={{ padding: "8px 6px" }}>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            style={{ padding: "6px" }}
          >
            {/* Left: Title */}
            <Grid item xs>
              <Typography variant="body1" noWrap>
                {title}
              </Typography>
            </Grid>

            {/* Right: Excel Icon */}
            {hasData && (
              <Grid item>
                <Tooltip title="Download Excel">
                  <span>
                    <IconButton
                      size="medium"
                      cursor="pointer"
                      onClick={downloadHandler}
                      disabled={downloading || data.length === 0}
                    >
                      <img
                        src={excelIcon}
                        alt="excel"
                        style={{ width: 25, height: 25 }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
            )}
          </Grid>
          <TableContainer
            className={matches ? classes.container2 : classes.container}
          >
            <Table
              stickyHeader
              aria-label="sticky table"
              className={classes.table}
              size={"small"}
            >
              <EnhancedTableHead
                classes={classes}
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={data.length}
                headCells={invoiceSummaryHeadCells}
              />

              <TableBody>
                {isLoading}
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      style={{ textAlign: "center" }}
                      rowSpan={6}
                      colSpan={13}
                    >
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : data.length === 0 ? (
                  <TableRow hover>
                    <TableCell
                      style={{ textAlign: "center" }}
                      rowSpan={6}
                      colSpan={13}
                    >
                      No record
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((row, index) => {
                    let amount = row?.invTotal - row?.balanceAmount;
                    let lilamount = row?.invTotal - row?.IPPaid;
                    return (
                      <TableRow hover key={index}>
                        <TableCell style={styles}>{index + 1}</TableCell>
                        <TableCell style={styles} component="th" scope="row">
                          <b>
                            {row.company || <CustomChip title={"Private"} />}
                          </b>
                        </TableCell>
                        <TableCell style={styles}>{row._id}</TableCell>
                        <TableCell style={styles} align="left">
                          {row.invoiceNo} <br />
                          <small>{row.invoiceDate}</small>
                        </TableCell>
                        <TableCell style={styles} align="left">
                          {parseFloat(row?.subTotal).toFixed(2)}
                        </TableCell>
                        <TableCell style={styles} align="left">
                          {parseFloat(row?.invAmount).toFixed(2)}
                        </TableCell>
                        <TableCell style={styles} align="left">
                          {row?.vat}
                        </TableCell>
                        {/* <TableCell style={styles} align="left">{(row?.vat * 0.6).toFixed(2)}</TableCell> */}
                        <TableCell style={styles} align="left">
                          {(row?.invAmount * 0.12).toFixed(2)}
                        </TableCell>
                        <TableCell style={styles} align="left">
                          {row?.outstanding.toFixed(2)}
                        </TableCell>
                        <TableCell style={styles} align="left">
                          {row?.pays.map((pay) => `${pay.paymentRef}, `)}
                        </TableCell>
                        <TableCell style={styles} align="left">
                          {row?.pays.map(
                            (pay) =>
                              `${moment(pay.paymentDate).format("DD/MM/YYYY")}, `,
                          )}
                        </TableCell>
                        <TableCell style={styles} align="left">
                          {row?.pays.map((pay) => `${pay.paidAmount}, `)}
                        </TableCell>
                        <TableCell style={styles} align="left">
                          {row?.pays.map((pay) => `${pay.notes}, `)}
                        </TableCell>
                        {/* <TableCell style={styles} align="left">{row?.paymentRef}</TableCell>
                                                <TableCell style={styles} align="left">{moment(row?.paymentDate).format('DD/MM/YYYY')}</TableCell>
                                                <TableCell style={styles} align="left">{row?.paidAmount}</TableCell> */}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </>
  );
};

export default React.memo(CardItem);
